import * as yup from 'yup';

import { PLATFORMS_REQUIRED_FIELDS, TRIM_ERROR, WHATSAPP_PLATFORM_ID } from '../constants';

/**
 * This function determines whether on a given platform,
 * a field is required/mandatory or not
 *
 * @param {boolean} active Flag indicating whether platform is active or not
 * @param {number} platformId ID of the platform
 * @param {string} fieldName name of the field we are deciding is required or not
 */
const isPlatformFieldRequired = (active, platformId, fieldName) => {
  if (
    !active ||
    !Object.prototype.hasOwnProperty.call(PLATFORMS_REQUIRED_FIELDS, platformId)
  )
    return false;

  return PLATFORMS_REQUIRED_FIELDS[platformId].requiredFields.includes(
    fieldName
  );
};

// This validation schema is for business manager v2
export const platformDeploymentSchemaV2 = yup.object().shape({
  active: yup.bool(),
  platform_id: yup
    .number()
    .when('active', (active, schema) =>
      !active ? schema.notRequired() : schema.required()
    ),

  callback_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when('active', (active, schema) =>
      !active ? schema.notRequired() : schema.required()
    ),

  access_token: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'access_token'))
        return schema.required(
          `Access token for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  app_secret_key: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'app_secret_key'))
        return schema.required(
          `App secret key for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  webhook_enabled: yup.bool,

  webhook_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(
      ['active', 'webhook_enabled', 'platform_id'],
      (active, webhook_enabled, platform_id, schema) => {
        if (
          webhook_enabled &&
          isPlatformFieldRequired(active, platform_id, 'webhook_url')
        ) {
          return schema.required(
            `Webhook URL for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
          );
        }

        return schema.notRequired();
      }
    ),

  webhook_email_alerts: yup.bool,

  emails: yup
    .array()
    .of(
      yup
        .string()
        .trim(TRIM_ERROR)
        .email('Invalid Email ID entered in platform deployments.')
    )
    .when(
      ['active', 'webhook_email_alerts', 'platform_id'],
      (active, webhook_email_alerts, platform_id, schema) => {
        if (
          webhook_email_alerts &&
          isPlatformFieldRequired(active, platform_id, 'emails')
        ) {
          return schema.required(
            `Email IDs for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
          );
        }

        return schema.notRequired();
      }
    ),

  whatsapp_base_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id', 'whatsapp_channel_identifier'], (active, platform_id, whatsapp_channel_identifier, schema) => {
      if (platform_id == WHATSAPP_PLATFORM_ID && whatsapp_channel_identifier) {
        return schema.notRequired();
      }

      if (isPlatformFieldRequired(active, platform_id, 'whatsapp_base_url')) {
        return schema.required(
          `Whatsapp base URL for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    }),

  whatsapp_password: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id', 'whatsapp_channel_identifier'], (active, platform_id, whatsapp_channel_identifier, schema) => {
      if (platform_id == WHATSAPP_PLATFORM_ID && whatsapp_channel_identifier) {
        return schema.notRequired();
      }

      if (isPlatformFieldRequired(active, platform_id, 'whatsapp_password')) {
        return schema.required(
          `Whatsapp password for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    }),

  whatsapp_username: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id', 'whatsapp_channel_identifier'], (active, platform_id, whatsapp_channel_identifier, schema) => {
      if (platform_id == WHATSAPP_PLATFORM_ID && whatsapp_channel_identifier) {
        return schema.notRequired();
      }

      if (isPlatformFieldRequired(active, platform_id, 'whatsapp_username')) {
        return schema.required(
          `Whatsapp username for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    }),

  whatsapp_phone_number: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (
        isPlatformFieldRequired(active, platform_id, 'whatsapp_phone_number')
      ) {
        return schema.required(
          `Phone number for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    })
    .test({
      test: (value) => {
        const numberRegex = new RegExp('^[0-9]+$');
        if (value && !numberRegex.test(value)) {
          return false;
        }
        return true;
      },
      message: `Enter valid Phone Number with Country Code for WhatsApp deployment.`,
    }),

  waba_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'waba_id')) {
        return schema.required(
          `WABA ID for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    })
    .test({
      test: (value) => {
        const numberRegex = new RegExp('^[0-9]+$');
        if (value && !numberRegex.test(value)) {
          return false;
        }
        return true;
      },
      message: `Enter valid WABA ID for WhatsApp deployment.`,
    }),

  whatsapp_pay_enabled: yup.bool(),

  whatsapp_pay_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(
      ['active', 'whatsapp_pay_enabled', 'platform_id'],
      (active, whatsapp_pay_enabled, platform_id, schema) => {
        if (
          whatsapp_pay_enabled &&
          isPlatformFieldRequired(active, platform_id, 'whatsapp_pay_url')
        ) {
          return schema.required(
            `Whatsapp pay url for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
          );
        }
        return schema.notRequired();
      }
    ),

  whatsapp_payment_config: yup
    .string()
    .trim(TRIM_ERROR)
    .when(
      ['active', 'whatsapp_pay_enabled', 'platform_id'],
      (active, whatsapp_pay_enabled, platform_id, schema) => {
        if (
          whatsapp_pay_enabled &&
          isPlatformFieldRequired(
            active,
            platform_id,
            'whatsapp_payment_config'
          )
        ) {
          return schema.required(
            `Whatsapp pay Id for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
          );
        }
        return schema.notRequired();
      }
    ),

  whatsapp_channel_identifier: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'whatsapp_channel_identifier')) {
        return schema.required(
          `Channel Identifier for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    })
    .test({
      test: (value) => {
        if (value && value.length > 128) {
          return false;
        }
        return true;
      },
      message: `Enter valid Channel Identifier max length of 100 chars`,
    }),

  client_name: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'client_name'))
        return schema.required(
          `Client Name for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  zd_client_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'zd_client_id'))
        return schema.required(
          `ZD Client ID for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  subdomain: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'subdomain'))
        return schema.required(
          `Subdomain for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  client_secret: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'client_secret'))
        return schema.required(
          `Client Secret for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  agent_offline_message: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'agent_offline_message'))
        return schema.required(
          `Agent offline message for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  telegram_base_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'telegram_base_url'))
        return schema.required(
          `Telegram base url for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  telegram_access_token: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'telegram_access_token'))
        return schema.required(
          `Telegram access token for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  domain_name: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'domain_name'))
        return schema.required(
          `Domain name for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  api_key: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'api_key'))
        return schema.required(
          `API key for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  api_token: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'api_token'))
        return schema.required(
          `API token for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  admin_token_enabled: yup.bool(),

  admin_token: yup
    .string()
    .trim(TRIM_ERROR)
    .when(
      ['active', 'platform_id', 'admin_token_enabled'],
      (active, platform_id, admin_token_enabled, schema) => {
        if (
          admin_token_enabled &&
          isPlatformFieldRequired(active, platform_id, 'admin_token')
        ) {
          return schema.required(
            `Admin API Token for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
          );
        }
        return schema.notRequired();
      }
    ),

  storefront_token_enabled: yup.bool(),

  storefront_token: yup
    .string()
    .trim(TRIM_ERROR)
    .when(
      ['active', 'platform_id', 'storefront_token_enabled'],
      (active, platform_id, storefront_token_enabled, schema) => {
        if (
          storefront_token_enabled &&
          isPlatformFieldRequired(active, platform_id, 'storefront_token')
        ) {
          return schema.required(
            `Storefront API Token for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
          );
        }
        return schema.notRequired();
      }
    ),

  teams_client_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'teams_client_id'))
        return schema.required(
          `Client id for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      return schema.notRequired();
    }),

  teams_client_secret: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'teams_client_secret'))
        return schema.required(
          `Client secret key for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  kms_type: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'kms_type'))
        return schema.required(
          `KMS Type for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  bot_name: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'bot_name'))
        return schema.required(
          `Bot Name for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  kms_settings: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'kms_settings'))
        return schema.required(
          `KMS Settings for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  base_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'base_url'))
        return schema.required(
          `Base URL for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  org_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'org_id'))
        return schema.required(
          `Organisation Id for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  deployment_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'deployment_id'))
        return schema.required(
          `Deployment Id for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  button_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'button_id'))
        return schema.required(
          `Button Id for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  chat_request_fail: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'chat_request_fail'))
        return schema.required(
          `Chat Request Fail for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  chat_request_pending: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'chat_request_pending'))
        return schema.required(
          `Chat Request Pending for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  chat_ended: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'chat_ended'))
        return schema.required(
          `Chat Ended for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  secret_key: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'secret_key'))
        return schema.required(
          `Secret Key for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  page_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'page_id'))
        return schema.required(
          `Instagram Page ID for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  secret_challenge_key: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'secret_challenge_key'))
        return schema.required(
          `Haptik Secret Challenge Key for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  infobip_base_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'infobip_base_url'))
        return schema.required(
          `Infobip Base URL for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  scenario_key: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'scenario_key'))
        return schema.required(
          `Infobip Scenario Key for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  auth_token: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'auth_token'))
        return schema.required(
          `Infobip Auth Token for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  bot_wa_number: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'bot_wa_number'))
        return schema.required(
          `Bot WA Number for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  channel_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'channel_id'))
        return schema.required(
          `Line channel id for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  channel_access_token: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'channel_access_token'))
        return schema.required(
          `Line Assertion Signing Key for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  line_client_secret: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'line_client_secret'))
        return schema.required(
          `Line client secret for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  ic_access_token: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'ic_access_token'))
        return schema.required(
          `Instagram Classic Access Token for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  ic_app_secret_key: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'ic_app_secret_key'))
        return schema.required(
          `Instagram Classic App Secret Key for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      return schema.notRequired();
    }),

  ic_secret_challenge_key: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (
        isPlatformFieldRequired(active, platform_id, 'ic_secret_challenge_key')
      )
        return schema.required(
          `Haptik Secret Challenge Key(Classic) for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      return schema.notRequired();
    }),

  ic_secret_key: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'ic_secret_key'))
        return schema.required(
          `Instagram Classic Secret Key for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      return schema.notRequired();
    }),

  ic_page_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'ic_page_id'))
        return schema.required(
          `Instagram Classic Page ID for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      return schema.notRequired();
    }),

  gorgias_widget_username: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (
        isPlatformFieldRequired(active, platform_id, 'gorgias_widget_username')
      )
        return schema.required(
          `Gorgias Widget User name for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  widget_api_base_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'widget_api_base_url'))
        return schema.required(
          `Gorgias API Base URL for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  gorgias_default_team_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (
        isPlatformFieldRequired(active, platform_id, 'gorgias_default_team_id')
      )
        return schema.required(
          `Gorgias Default Team ID for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  haptik_iva_gorgias_email: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (
        isPlatformFieldRequired(active, platform_id, 'haptik_iva_gorgias_email')
      )
        return schema.required(
          `Gorgias Haptik IVA Gorgias Email for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  haptik_iva_gorgias_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'haptik_iva_gorgias_id'))
        return schema.required(
          `Gorgias Haptik IVA Gorgias Id for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  vc_user_name: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'vc_user_name'))
        return schema.required(
          `User Name for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  vc_password: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'vc_password'))
        return schema.required(
          `Password for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  vc_api_key: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'vc_api_key'))
        return schema.required(
          `Api Key for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  twilio_password: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'twilio_password')) {
        return schema.required(
          `Password for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    }),

  sms_destination_application_id: yup
    .object()
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (
        isPlatformFieldRequired(
          active,
          platform_id,
          'sms_destination_application_id'
        )
      ) {
        return schema.required(
          `SMS Destination App for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    }),

  twilio_account_sid: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'twilio_account_sid')) {
        return schema.required(
          `Twilio Account for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    }),

  twilio_mobile_no: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'twilio_mobile_no')) {
        return schema.required(
          `Phone number for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    })
    .test({
      test: (value) => {
        const numberRegex = new RegExp('^[+][0-9]+$');
        if (value && !numberRegex.test(value)) {
          return false;
        }
        return true;
      },
      message: `Enter valid Phone Number with Country Code for Twilio deployment.`,
    }),

  ct_webhook_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'ct_webhook_url')) {
        return schema.required(
          `Clevertap Webhook URL for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }
    }),

  haptik_username: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'haptik_username'))
        return schema.required(
          `Haptik user name for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  haptik_password: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'haptik_password'))
        return schema.required(
          `Haptik password for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  magento_domain: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'magento_domain'))
        return schema.required(
          `Magento domain for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  magento_buzzo_mapping: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'magento_buzzo_mapping'))
        return schema.required(
          `Magento buzzo mapping for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  magento_media_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'magento_media_url'))
        return schema.required(
          `Magento media url for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  magento_storeview: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'magento_storeview'))
        return schema.required(
          `Magento store view for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );

      return schema.notRequired();
    }),

  wc_domain: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'wc_domain')) {
        return schema.required(
          `Woocommerce domain for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }
    }),

  wc_username: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'wc_username')) {
        return schema.required(
          `Woocommerce username for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }
    }),

  wc_password: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'wc_password')) {
        return schema.required(
          `Woocommerce password for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }
    }),

  radisys_base_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'radisys_base_url')) {
        return schema.required(
          `Base URL for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }
    }),

  microsoft_dynamic_client_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'microsoft_dynamic_client_id')) {
        return schema.required(
          `Microsoft Dynamic Client ID for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }
    }),

  microsoft_dynamic_client_secret: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'microsoft_dynamic_client_secret')) {
        return schema.required(
          `Microsoft Dynamic Client Secret for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }
    }),

  microsoft_dynamic_resource_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'microsoft_dynamic_resource_url')) {
        return schema.required(
          `Microsoft Dynamic Resource URL for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }
    }),

  microsoft_dynamic_service_root_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'microsoft_dynamic_service_root_url')) {
        return schema.required(
          `Microsoft Dynamic Service Root URL for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }
    }),
});
const platformDeploymentSchema = yup.object().shape({
  active: yup.bool(),
  platformId: yup
    .number()
    .when('active', (active, schema) =>
      !active ? schema.notRequired() : schema.required()
    ),

  callbackURL: yup
    .string()
    .trim(TRIM_ERROR)
    .when('active', (active, schema) =>
      !active ? schema.notRequired() : schema.required()
    ),

  access_token: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platformId'], (active, platformId, schema) => {
      if (isPlatformFieldRequired(active, platformId, 'access_token'))
        return schema.required(
          `Access token for ${PLATFORMS_REQUIRED_FIELDS[platformId].name} deployment is required`
        );

      return schema.notRequired();
    }),

  app_secret_key: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platformId'], (active, platformId, schema) => {
      if (isPlatformFieldRequired(active, platformId, 'app_secret_key'))
        return schema.required(
          `App secret key for ${PLATFORMS_REQUIRED_FIELDS[platformId].name} deployment is required`
        );

      return schema.notRequired();
    }),

  webhook_enabled: yup.bool,

  webhook_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(
      ['active', 'webhook_enabled', 'platformId'],
      (active, webhook_enabled, platformId, schema) => {
        if (
          webhook_enabled &&
          isPlatformFieldRequired(active, platformId, 'webhook_url')
        ) {
          return schema.required(
            `Webhook URL for ${PLATFORMS_REQUIRED_FIELDS[platformId].name} deployment is required`
          );
        }

        return schema.notRequired();
      }
    ),

  webhook_email_alerts: yup.bool,

  emails: yup
    .array()
    .of(
      yup
        .string()
        .trim(TRIM_ERROR)
        .email('Invalid Email ID entered in platform deployments.')
    )
    .when(
      ['active', 'webhook_email_alerts', 'platformId'],
      (active, webhook_email_alerts, platformId, schema) => {
        if (
          webhook_email_alerts &&
          isPlatformFieldRequired(active, platformId, 'emails')
        ) {
          return schema.required(
            `Email IDs for ${PLATFORMS_REQUIRED_FIELDS[platformId].name} deployment is required`
          );
        }

        return schema.notRequired();
      }
    ),

  whatsapp_base_url: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platformId', 'whatsapp_channel_identifier'], (active, platformId, whatsapp_channel_identifier, schema) => {
      if (platformId == WHATSAPP_PLATFORM_ID && whatsapp_channel_identifier) {
        return schema.notRequired();
      }

      if (isPlatformFieldRequired(active, platformId, 'whatsapp_base_url')) {
        return schema.required(
          `Whatsapp base URL for ${PLATFORMS_REQUIRED_FIELDS[platformId].name} deployment is required`
        );
      }

      return schema.notRequired();
    }),

  whatsapp_password: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platformId', 'whatsapp_channel_identifier'], (active, platformId, whatsapp_channel_identifier, schema) => {
      if (platformId == WHATSAPP_PLATFORM_ID && whatsapp_channel_identifier) {
        return schema.notRequired();
      }

      if (isPlatformFieldRequired(active, platformId, 'whatsapp_password')) {
        return schema.required(
          `Whatsapp password for ${PLATFORMS_REQUIRED_FIELDS[platformId].name} deployment is required`
        );
      }

      return schema.notRequired();
    }),

  whatsapp_username: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platformId', 'whatsapp_channel_identifier'], (active, platformId, whatsapp_channel_identifier, schema) => {
      if (platformId == WHATSAPP_PLATFORM_ID && whatsapp_channel_identifier) {
        return schema.notRequired();
      }

      if (isPlatformFieldRequired(active, platformId, 'whatsapp_username')) {
        return schema.required(
          `Whatsapp username for ${PLATFORMS_REQUIRED_FIELDS[platformId].name} deployment is required`
        );
      }

      return schema.notRequired();
    }),

  whatsapp_phone_number: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (
        isPlatformFieldRequired(active, platform_id, 'whatsapp_phone_number')
      ) {
        return schema.required(
          `Phone number for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    })
    .test({
      test: (value) => {
        const numberRegex = new RegExp('^[0-9]+$');
        if (value && !numberRegex.test(value)) {
          return false;
        }
        return true;
      },
      message: `Enter valid Phone Number with Country Code for WhatsApp deployment.`,
    }),

  waba_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'waba_id')) {
        return schema.required(
          `WABA ID for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    })
    .test({
      test: (value) => {
        const numberRegex = new RegExp('^[0-9]+$');
        if (value && !numberRegex.test(value)) {
          return false;
        }
        return true;
      },
      message: `Enter valid WABA ID for WhatsApp deployment.`,
    }),

  twilio_password: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'twilio_password')) {
        return schema.required(
          `Password for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    }),

  sms_destination_application_id: yup
    .object()
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (
        isPlatformFieldRequired(
          active,
          platform_id,
          'sms_destination_application_id'
        )
      ) {
        return schema.required(
          `SMS Destination App for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    }),

  twilio_account_sid: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'twilio_account_sid')) {
        return schema.required(
          `Twilio Account for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    }),

  twilio_mobile_no: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'twilio_mobile_no')) {
        return schema.required(
          `Phone number for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    })
    .test({
      test: (value) => {
        const numberRegex = new RegExp('^[+][0-9]+$');
        if (value && !numberRegex.test(value)) {
          return false;
        }
        return true;
      },
      message: `Enter valid Phone Number with Country Code for Twilio deployment.`,
    }),

    assistant_id: yup
    .string()
    .trim(TRIM_ERROR)
    .when(['active', 'platform_id'], (active, platform_id, schema) => {
      if (isPlatformFieldRequired(active, platform_id, 'assistant_id')) {
        return schema.required(
          `Assistant ID for ${PLATFORMS_REQUIRED_FIELDS[platform_id].name} deployment is required`
        );
      }

      return schema.notRequired();
    })
    .test({
      test: (value) => {
        const numberRegex = new RegExp('^[+][0-9]+$');
        if (value && !numberRegex.test(value)) {
          return false;
        }
        return true;
      },
      message: `Enter valid Assistant ID for RCS deployment.`,
    }),

});

export default platformDeploymentSchema;
